import React from 'react';
import PropTypes from 'prop-types';
import { Config, I18n, I18nContext } from '@deporvillage/utils-library-component';
import Modal from '@deporvillage/modal-component';
import Picture from '@deporvillage/picture-component';
import Swiper, { SwiperArrowType, SwiperClick } from '@deporvillage/swiper-component';

import styles from './PhotoSlurp.module.scss';

const Click = {
    Gallery: 'gallery',
    GalleryPrev: 'gallery-prev',
    GalleryNext: 'gallery-next',
    Picture: 'picture',
    Tag: 'tag',
};

const PhotoSlurpApi = 'https://api.photoslurp.com/v1/photos/';
const PhotoSlurpId = 918;
const PhotoSlurpLimit = 20;

const SliderBreakpoints = {
    0: {
        slidesPerView: 'auto',
        spaceBetween: 1,
    },
};

const SliderBreakpointsFullScreen = {
    0: {
        slidesPerView: 1,
        freeMode: false,
    },
};

const sanitize = (items) => items.filter((item) => {
    if (!Object.keys(item).some((k) => !item[k] && item[k] !== '')) return item;
    return false;
});

const photoSlurpMapper = (res) => res.map(
    ({
        id,
        title,
        images,
        source_username: user,
        source_url: url,
    }) => ({
        id,
        title,
        images,
        user,
        url,
    }),
);

const getPhotoSlurp = async ({ lang }) => {
    const res = await fetch(
        `${PhotoSlurpApi}?album=${PhotoSlurpId}&lang=${lang}&page_limit=${PhotoSlurpLimit}&allow_empty=false`,
        { priority: 'low' },
    );
    return res.json();
};

const PhotoSlurp = ({ onClick }) => {
    const { lang, isMobile } = React.useContext(Config);
    const { translate } = React.useContext(I18nContext);

    const [items, setItems] = React.useState(null);
    const [openModal, setOpenModal] = React.useState(false);
    const [activeImage, setActiveImage] = React.useState(0);

    const handleOnClickViewGallery = () => {
        onClick && onClick(Click.Gallery);
        window.location.replace(translate('home_page.photo_slurp.url_gallery'));
    };

    const handleOnClickPicture = (i) => {
        setActiveImage(i);
        setOpenModal(true);
        onClick && onClick(Click.Picture);
    };

    const handleOnClickSwiper = (t) => {
        switch (t) {
            case SwiperClick.PREV:
                onClick && onClick(Click.GalleryPrev);
                break;
            case SwiperClick.NEXT:
                onClick && onClick(Click.GalleryNext);
                break;
        }
    };

    const handleLoadPhotoSlurp = async () => {
        const res = await getPhotoSlurp({ lang });
        setItems(sanitize(photoSlurpMapper(res?.results)));
    };

    React.useEffect(() => {
        handleLoadPhotoSlurp();
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, []);

    return (
        <div
            className={styles['photo-slurp-component']}
            data-testid='photo-slurp-component'
        >
            <div className={styles.header}>
                <h2><I18n>home_page.photo_slurp.title</I18n></h2>
                <a
                    className={styles.tag}
                    data-testid={'ps-tag'}
                    target={'_blank'}
                    rel='noopener noreferrer'
                    href={translate('home_page.photo_slurp.url')}
                    onClick={() => onClick && onClick(Click.Tag)}
                    title={translate('home_page.photo_slurp.url_title')}
                >
                    <i className={`${styles.icon} icon icon-instagram`} /> @deporvillage
                </a>
            </div>

            <div className={styles.slider}>
                {
                    items && (
                        <Swiper
                            breakpoints={SliderBreakpoints}
                            multiple
                            arrows={isMobile ? SwiperArrowType.None : SwiperArrowType.Out}
                            click={handleOnClickSwiper}
                        >
                            {
                                items.map(({ id, images, user }, i) => (
                                    <div
                                        key={id}
                                        data-testid={`ps-slide-${i}`}
                                        className={styles.slide}
                                        onClick={() => handleOnClickPicture(i)}
                                    >
                                        <Picture
                                            src={images?.small}
                                            lazy
                                            name={user}
                                            className={styles.fit}
                                        />
                                    </div>
                                ))
                            }
                        </Swiper>
                    )
                }
            </div>

            <div className={styles.footer}>
                <div className={styles['footer-right']} data-testid='redirect-gallery'>
                    <button onClick={handleOnClickViewGallery} className={styles.button}>
                        <I18n>home_page.photo_slurp.show_gallery</I18n>
                    </button>
                </div>
            </div>

            {
                openModal && (
                    <Modal onClose={() => setOpenModal(false)} fullscreen={isMobile}>
                        <div className={styles.fullscreen}>
                            <Swiper
                                initial={activeImage}
                                breakpoints={SliderBreakpointsFullScreen}
                                arrows={isMobile ? SwiperArrowType.None : SwiperArrowType.Out}
                            >
                                {
                                    items.map(({
                                        id, images, title, url, user,
                                    }, i) => (
                                        <div key={id} className={styles['fullscreen-wrapper']} data-testid={`ps-fscreen-${i}`}>
                                            <div className={styles['fullscreen-image']}>
                                                <Picture src={images?.medium} lazy name={title} />
                                            </div>
                                            <div className={styles['fullscreen-content']}>
                                                <a className={styles['fullscreen-username']}
                                                    href={url}
                                                    target={'_blank'}
                                                    rel='noopener noreferrer'>
                                                    {user || 'Instagram user'}
                                                </a>
                                                <div className={styles['fullscreen-title']}>
                                                    {title}
                                                </div>
                                                <div className={styles['fullscreen-tag']}>
                                                    <i className={`${styles.icon} icon icon-instagram`} /> @deporvillage
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                }
                            </Swiper>
                        </div>
                    </Modal>
                )
            }
        </div>
    );
};

PropTypes.PhotoSlurp = {
    onClick: PropTypes.func,
};

export default PhotoSlurp;
export { Click as PhotoSlurpClick };
